import _ from 'lodash'
import Mustache from 'mustache'

import CONSTANT_MESSAGES from 'core/data-model/constants/messages'
import MESSAGES from 'core/data-model/constants/validation-messages'
import VALUE from 'core/data-model/constants/values'
import { STANDARD_OCCUPATION } from 'core/service/insured'
import { createChecker } from 'core/service/lib/check'
import { d } from 'core/service/lib/decimal'
import { formatNumber, roundDown } from 'core/service/lib/number-format'

import { ageInYears } from '../../lib/age-format'

import {
  validateMinTopUpValueRule,
  validateMinPremiumValueRule,
  validateMaxRegularTopUpRule,
  validateSumAssuredRule,
} from './rules'

const regularPremiumChecker = createChecker([
  [
    'greaterThanMinimum',
    {
      type: 'VALIDATION',
      rule: validateMinPremiumValueRule,
      message: (paymentRange) => {
        const msg = _.get(paymentRange, ['messages'])
        return typeof msg === 'string' ? msg : _.get(msg, 'min', '')
      },
    },
  ],
])

const validateMinPremium = (paymentRange, premiumAmount) =>
  validateMinTopUpValueRule(paymentRange, premiumAmount.regularTopUp)

const topUpChecker = createChecker([
  [
    'greaterThanMinimum',
    {
      type: 'VALIDATION',
      rule: validateMinPremium,
      message: (paymentRange) => {
        let paymentFrequency = _.get(paymentRange, 'paymentFrequency', '')
        let paymentFrequencyInThai = _.get(CONSTANT_MESSAGES, _.snakeCase(paymentFrequency).toUpperCase())
        return Mustache.render(MESSAGES.RULE_IWEALTHY_MIN_TOPUP_PREMIUM, {
          paymentFrequency: paymentFrequencyInThai,
          minRegularTopUp: formatNumber(_.get(paymentRange, 'min', 0)),
        })
      },
    },
  ],
  [
    'lessThanMaximum',
    {
      type: 'VALIDATION',
      rule: validateMaxRegularTopUpRule,
      message: MESSAGES.RULE_IWEALTHY_MAX_TOPUP_PREMIUM,
    },
  ],
])

export const getRegularPaymentRange = async (
  getBasicPlan,
  displayProductQuery,
  paymentFrequency,
  premiumType,
  insuredAge
) => {
  const basicPlan = await getBasicPlan(displayProductQuery)
  const paymentRange = _.get(basicPlan, ['paymentRanges', premiumType, _.camelCase(paymentFrequency)], { min: 0 })
  return _.isArray(paymentRange)
    ? _.find(
        paymentRange,
        (paymentrange) =>
          insuredAge >= _.get(paymentrange, ['age', 'min'], 0) && insuredAge <= _.get(paymentrange, ['age', 'max'], 0)
      )
    : paymentRange
}

export const getPaymentFrequency = async (getModelFactorById, modelFactorId) => {
  const modelFactor = await getModelFactorById(modelFactorId)
  return modelFactor.paymentFrequency
}

const sumAssuredMessage = (sumAssuredrange, sumAssuredPremium) => {
  let messageKey =
    sumAssuredrange.min === sumAssuredrange.max
      ? 'RULE_IWEALTHY_EXPECTED_SUM_ASSURED'
      : 'RULE_IWEALTHY_SUM_ASSURED_LIMIT'

  let annualRegularPremium = sumAssuredPremium.annualRegularPremium
  return Mustache.render(MESSAGES[messageKey], {
    minSumAssured: formatNumber(annualRegularPremium * sumAssuredrange.min),
    maxSumAssured: formatNumber(annualRegularPremium * sumAssuredrange.max),
  })
}

const sumAssuredChecker = createChecker([
  [
    'withinRangeValidation',
    {
      type: 'VALIDATION',
      rule: validateSumAssuredRule,
      message: sumAssuredMessage,
    },
  ],
  [
    'withinRangeInformation',
    {
      type: 'ADDITIONAL_INFORMATION',
      rule: _.negate(validateSumAssuredRule),
      message: sumAssuredMessage,
    },
  ],
])

export const _getForSumassuredWithOccupationFactor = (occupationFactor = 0, productCode, { min, max }) => {
  if (occupationFactor === STANDARD_OCCUPATION || productCode !== VALUE.RPUDR) {
    return { min, max }
  }
  return _calculateSumassuredWithOccupationFactor(occupationFactor, { min, max })
}

export const _calculateSumassuredWithOccupationFactor = (occupationFactor, { min, max }) => {
  const tempLoading = d(occupationFactor)
  const convertedMin = d(min)
  const convertedMax = d(max)
  const minResult = roundDown(convertedMin / (1 + convertedMin * (tempLoading / 1000) * 1.15), 0)
  const maxResult = roundDown(convertedMax / (1 + convertedMax * (tempLoading / 1000) * 1.15), 0)
  return {
    min: minResult,
    max: maxResult,
  }
}

export const _getRegularPremiumMultiplier = (insuredSumAssuredLimit, code, gender) => {
  if (code === VALUE.RPUDR) {
    return _getRegularPremiumMultiplierRPUDR(insuredSumAssuredLimit, gender)
  } else {
    return _getRegularPremiumMultiplierRPUL(insuredSumAssuredLimit)
  }
}

export const _getRegularPremiumMultiplierRPUDR = (insuredSumAssuredLimit, gender) => {
  return insuredSumAssuredLimit
    ? _.get(insuredSumAssuredLimit, ['regularPremiumMultiplier', gender], { min: 0, max: 0 })
    : { min: 0, max: 0 }
}

export const _getRegularPremiumMultiplierRPUL = (insuredSumAssuredLimit) => {
  return insuredSumAssuredLimit
    ? _.get(insuredSumAssuredLimit, ['regularPremiumMultiplier'], { min: 0, max: 0 })
    : { min: 0, max: 0 }
}

export const _getInsuredSumAssuredLimit = (sumAssuredLimits, insuredAgeInYears) => {
  return _.find(sumAssuredLimits, ({ age }) => insuredAgeInYears >= age.min && insuredAgeInYears <= age.max)
}

/**
 * @param dataWrapper {ReturnType<import('core/data/data-wrapper')._DB>}
 * @param displayProductService {ReturnType<import('core/service/display-product/display-product').createDisplayProductService>}
 */
export function createInvestmentCoverageValidateService(dataWrapper, displayProductService) {
  const validateRegularPremium = async (displayProductQuery, modelFactorId, regularPremium, insuredAge) => {
    let paymentFrequency = await getPaymentFrequency(dataWrapper.getModelFactorById, modelFactorId)
    let paymentRange = await getRegularPaymentRange(
      displayProductService.getBasicPlan,
      displayProductQuery,
      paymentFrequency,
      'regularPremium',
      insuredAge.value
    )
    return _validateRegularPremium(paymentRange, regularPremium)
  }

  const _validateRegularPremium = async (paymentRange, regularPremium) => {
    return regularPremiumChecker(paymentRange, regularPremium)
  }

  const validateRegularTopUp = async (displayProductQuery, modelFactorId, premiumAmount) => {
    let paymentFrequency = await getPaymentFrequency(dataWrapper.getModelFactorById, modelFactorId)
    let paymentRange = await getRegularPaymentRange(
      displayProductService.getBasicPlan,
      displayProductQuery,
      paymentFrequency,
      'regularTopUp'
    )
    let paymentRangeWithFrequency = { ...paymentRange, paymentFrequency }
    return _validateRegularTopUp(paymentRangeWithFrequency, premiumAmount)
  }

  const _validateRegularTopUp = async (paymentRange, premiumAmount) => {
    return topUpChecker(paymentRange, premiumAmount)
  }

  const validateSumAssured = async (
    displayProductQuery,
    modelFactorId,
    { sumAssured, regularPremium, insuredAge, insuredGender, occupationFactor }
  ) => {
    return _validateSumAssured(
      displayProductService.getBasicPlan,
      dataWrapper.getModelFactorById,
      displayProductQuery,
      modelFactorId,
      sumAssured,
      regularPremium,
      insuredAge,
      insuredGender,
      occupationFactor
    )
  }

  const _validateSumAssured = async (
    getBasicPlan,
    getModelFactorById,
    displayProductQuery,
    modelFactorId,
    sumAssured,
    regularPremium,
    insuredAge,
    insuredGender,
    occupationFactor
  ) => {
    const basicPlan = await getBasicPlan(displayProductQuery)
    let modelFactor = await getModelFactorById(modelFactorId)
    const productCode = basicPlan.productCode
    let sumAssuredLimits = _.get(basicPlan, ['paymentRanges', 'assured'])
    let insuredAgeInYears = ageInYears(insuredAge)

    const insuredSumAssuredLimit = _getInsuredSumAssuredLimit(sumAssuredLimits, insuredAgeInYears)
    const regularPremiumMultiplier = _getRegularPremiumMultiplier(
      insuredSumAssuredLimit,
      basicPlan.productCode,
      insuredGender
    )

    const regularPremiumMultiplierAfterCalculated = _getForSumassuredWithOccupationFactor(
      occupationFactor,
      basicPlan.productCode,
      {
        min: regularPremiumMultiplier.min,
        max: regularPremiumMultiplier.max,
      }
    )

    let annualRegularPremium = regularPremium * modelFactor.periods

    return sumAssuredChecker(regularPremiumMultiplierAfterCalculated, { annualRegularPremium, sumAssured, productCode })
  }

  const getMaximumSumAssured = async (
    displayProductQuery,
    modelFactorId,
    { regularPremium, insuredAge, insuredGender, occupationFactor }
  ) => {
    return _getMaximumSumAssured(
      displayProductService.getBasicPlan,
      dataWrapper.getModelFactorById,
      displayProductQuery,
      modelFactorId,
      regularPremium,
      insuredAge,
      insuredGender,
      occupationFactor
    )
  }

  const _getMaximumSumAssured = async (
    getBasicPlan,
    getModelFactorById,
    displayProductQuery,
    modelFactorId,
    regularPremium,
    insuredAge,
    insuredGender,
    occupationFactor
  ) => {
    const basicPlan = await getBasicPlan(displayProductQuery)
    const modelFactor = await getModelFactorById(modelFactorId)
    const sumAssuredLimits = _.get(basicPlan, ['paymentRanges', 'assured'])
    const insuredAgeInYears = ageInYears(insuredAge)

    const insuredSumAssuredLimit = _getInsuredSumAssuredLimit(sumAssuredLimits, insuredAgeInYears)
    const regularPremiumMultiplier = _getRegularPremiumMultiplier(
      insuredSumAssuredLimit,
      basicPlan.productCode,
      insuredGender
    )

    const regularPremiumMultiplierAfterCalculated = _getForSumassuredWithOccupationFactor(
      occupationFactor,
      basicPlan.productCode,
      {
        min: regularPremiumMultiplier.min,
        max: regularPremiumMultiplier.max,
      }
    )

    const annualRegularPremium = d(regularPremium).times(d(modelFactor.periods))
    const maxSumAssured = d(annualRegularPremium).times(d(regularPremiumMultiplierAfterCalculated.max))

    return maxSumAssured.toNumber()
  }

  const getRegularPremiumMaxMultiplier = async (displayProductQuery, { insuredAge, insuredGender }) => {
    return _getRegularPremiumMaxMultiplier(
      displayProductService.getBasicPlan,
      displayProductQuery,
      insuredAge,
      insuredGender
    )
  }

  const _getRegularPremiumMaxMultiplier = async (getBasicPlan, displayProductQuery, insuredAge, insuredGender) => {
    const basicPlan = await getBasicPlan(displayProductQuery)
    const sumAssuredLimits = _.get(basicPlan, ['paymentRanges', 'assured'])
    const insuredAgeInYears = ageInYears(insuredAge)
    const insuredSumAssuredLimit = _getInsuredSumAssuredLimit(sumAssuredLimits, insuredAgeInYears)

    const regularPremiumMultiplier = _getRegularPremiumMultiplier(
      insuredSumAssuredLimit,
      basicPlan.productCode,
      insuredGender
    )

    return regularPremiumMultiplier.max
  }

  return {
    validateRegularPremium,
    _validateRegularPremium,
    validateRegularTopUp,
    _validateRegularTopUp,
    validateSumAssured,
    _validateSumAssured,
    getMaximumSumAssured,
    _getMaximumSumAssured,
    getRegularPremiumMaxMultiplier,
    _getRegularPremiumMaxMultiplier,
  }
}
